// Common
import DataTable from "@components/DataTable";
import type { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import { Nav } from "react-bootstrap";
import { Button, Card, Form } from "react-bootstrap";
import FormFieldLabel from "@components/Forms/FormFieldLabel/FormFieldLabel";
import SearchField from "@components/Forms/SearchField/SearchField";
import { faTrashCan, faClockRotateLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  useDeleteSalesExecutiveMutation,
  useUpdateSalesExecutiveMutation,
} from "@generated";
// SalesExecutive Specific
import useListSalesExecutives from "./api";
import { SalesExecutive } from "./types";

const List = (): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [activeFilter, setActiveFilter] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<{ name?: string; active?: boolean }>({
    active: true,
  });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const [updateSalesExecutive, {}] = useUpdateSalesExecutiveMutation();
  const [deleteSalesExecutive, {}] = useDeleteSalesExecutiveMutation();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentTab, setCurrentTab] = useState<string>("active");
  const [counts, setCounts] = useState({
    active: null,
    archived: null,
  });

  const { salesExecutives, pageMeta, isLoading, refetch } =
    useListSalesExecutives({
      page: {
        number: pageNumber,
        size: pageSize,
      },
      filter,
    });

  const handleDelete = () => {
    setShowConfirmationModal(true);
  };

  const handleConfirm = () => {
    selectedIds.forEach((seId) => {
      if (currentTab == "active") {
        deleteSalesExecutive({
          slug: accountSlug,
          id: parseInt(seId),
        });
      } else if (currentTab == "archived") {
        let selected = salesExecutives.find((se) => se.id == parseInt(seId));

        updateSalesExecutive({
          slug: accountSlug,
          id: parseInt(seId),
          body: {
            data: {
              id: parseInt(seId),
              type: "sales-executives",
              attributes: {
                name: selected?.name || "",
                active: true,
              },
            } as any,
          },
        });
      }
    });
    setSelectedIds([]);
    refetch();
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    setShowConfirmationModal(false);
  };

  const searchByName = (name: string, active: boolean) => {
    setFilter({ name, active });
  };

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    searchByName(searchTerm, activeFilter);
  };

  const columns: ColumnDef<SalesExecutive>[] = [
    {
      header: "Name",
      cell(props) {
        return (
          <div
            className="salesExecutiveName"
            onClick={() => {
              window.location.assign(
                `/admin/sales_executives/${props.row.original.id}/edit`
              );
            }}
          >
            {props.row.original.name}
          </div>
        );
      },
    },
    {
      header: "Email",
      cell(props) {
        return <div>{props.row.original.email}</div>;
      },
    },
    {
      header: "Title",
      cell(props) {
        return <div>{props.row.original.title}</div>;
      },
    },
    {
      header: "Phone",
      cell(props) {
        return <div>{props.row.original.phone}</div>;
      },
    },
  ];

  if (isLoading) {
    return <ScopeStackSpinner />;
  } else {
    return (
      <div className="salesExecutivesTable">
        <Card
          style={{
            padding: "10px",
            borderRadius: "4px",
            border: "1px solid var(--gray-3-stroke, #D7D7D7)",
            background: " var(--white, #FFF)",
            boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Card.Header
            style={{
              backgroundColor: "#fff",
              lineHeight: "unset",
              display: "block",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div className="accordianTitle" style={{ margin: "0" }}>
                Sales Executives
              </div>
              <Button
                className="addBtn"
                onClick={() =>
                  window.location.assign("/admin/sales_executives/new")
                }
              >
                Add A Sales Executive
              </Button>
            </div>
            <hr></hr>
            <div
              style={{
                display: "flex",
              }}
            >
              {selectedIds.length > 0 && (
                <Button
                  style={{
                    marginRight: "10px",
                    alignSelf: "flex-end",
                    background: "unset",
                    color: "var(--teal-1, #418172)",
                    fontWeight: "700",
                    width: "11em",
                    height: "40px",
                    borderRadius: "4px",
                    border: "1px solid var(--gray-3-stroke, #D7D7D7)",
                  }}
                  onClick={handleDelete}
                >
                  {selectedIds.length} Selected{" "}
                  <span
                    style={{
                      borderLeft: "1px solid #D7D7D7",
                      height: "24px",
                      margin: "0 8px",
                    }}
                  ></span>
                  {currentTab == "active" && (
                    <FontAwesomeIcon
                      style={{ color: "black" }}
                      icon={faTrashCan}
                    />
                  )}
                  {currentTab == "archived" && (
                    <FontAwesomeIcon
                      style={{ color: "black" }}
                      icon={faClockRotateLeft}
                    />
                  )}
                </Button>
              )}

              <div style={{ width: "-webkit-fill-available" }}>
                <FormFieldLabel
                  label="Search Sales Executives"
                  className={undefined}
                />
                <Form
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearch(e);
                    }
                  }}
                  onSubmit={handleSearch}
                >
                  <SearchField
                    placeholder="Search by name"
                    onChange={(e) => {
                      setPageNumber(1);
                      setSearchTerm(e.target.value);
                    }}
                    value={searchTerm}
                    onClick={() => {
                      setSearchTerm("");
                      searchByName("", activeFilter);
                    }}
                    fullWidth
                  />
                </Form>
              </div>
            </div>
          </Card.Header>
          <Nav
            variant="underline"
            defaultActiveKey="active"
            onSelect={(k) => {
              setCurrentTab(k || "active");
              setSelectedIds([]);
              setActiveFilter(k == "active");
              setPageNumber(1);
              searchByName(searchTerm, k == "active");
            }}
          >
            <Nav.Item>
              <Nav.Link eventKey="active" className="nav-link-sales-executive">
                {`Active ${
                  counts?.active !== null && currentTab == "active"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="archived"
                className="nav-link-sales-executive"
              >
                {`Archived ${
                  counts?.archived !== null && currentTab == "archived"
                    ? `(${pageMeta.recordCount})`
                    : ""
                }`}
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <DataTable
            data={salesExecutives}
            columns={columns}
            selectable
            bordered
            striped
            hover
            totalPages={pageMeta.pageCount}
            totalRows={pageMeta.recordCount}
            currentPage={pageNumber}
            setCurrentPage={setPageNumber}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            paginationEnabled
            pageSizeEnabled
          />
        </Card>
        <ConfirmationModal
          title="Confirm Delete"
          message={`Are you sure that you want to ${currentTab == "active" ? "archive" : "restore"} ${selectedIds.length} sales executives?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          show={showConfirmationModal}
          onHide={handleCancel}
        />
      </div>
    );
  }
};

export default List;
